import { XIcon } from 'lucide-react-native';
import { ReactNode, useCallback } from 'react';

import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { useColors } from '@/hooks/useColors';
import { useHaptic } from '@/hooks/useHaptic';

/**
 * Shared destructive-or-affirmative confirmation modal. Matches the
 * hand-rolled "Delete draft" pattern: title statement in the header
 * with a close affordance, body for context, and a vertically stacked
 * footer with the primary action on top and an outlined secondary
 * cancel below. Both action buttons are full-width so the user can
 * tap the safe choice reflexively without crossing the screen.
 */
export function ConfirmModal({
  isOpen,
  title,
  description,
  acceptText = 'Confirm',
  cancelText = 'Cancel',
  acceptAction = 'primary',
  actionInProgress = false,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  title: string;
  description: string | ReactNode;
  acceptText: string;
  acceptAction?: 'primary' | 'secondary' | 'positive' | 'negative';
  cancelText?: string;
  actionInProgress?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { colors } = useColors();

  const haptic = useHaptic();

  const handleConfirm = useCallback(() => {
    if (acceptAction === 'negative') {
      haptic.warning();
    } else if (acceptAction === 'positive') {
      haptic.success();
    } else {
      haptic.impactMedium();
    }

    onConfirm();
  }, [acceptAction, haptic, onConfirm]);

  return (
    <Modal isOpen={isOpen} size="md" onClose={onCancel}>
      <ModalBackdrop />

      <ModalContent>
        <ModalHeader className="pb-5">
          <Heading size="h2-xl">{title}</Heading>
          <ModalCloseButton onPress={onCancel}>
            <Icon as={XIcon} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody className="pb-6">
          {typeof description === 'string' ? (
            <Text className="text-typography-700">{description}</Text>
          ) : (
            description
          )}
        </ModalBody>

        <ModalFooter className="w-full flex-col justify-start gap-3 pt-0">
          <Button
            action={acceptAction}
            className={
              acceptAction === 'negative'
                ? 'w-full border-warning-600 bg-warning-600 data-[hover=true]:border-warning-700 data-[hover=true]:bg-warning-700 data-[active=true]:border-warning-800 data-[active=true]:bg-warning-800'
                : 'w-full'
            }
            isDisabled={actionInProgress}
            onPress={handleConfirm}>
            {actionInProgress ? <ButtonSpinner color={colors.typography[0]} /> : null}
            <ButtonText className="text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0">
              {acceptText}
            </ButtonText>
          </Button>

          <Button
            className="w-full"
            variant="outline"
            action="secondary"
            isDisabled={actionInProgress}
            onPress={onCancel}>
            <ButtonText>{cancelText}</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
